import { useState, useEffect, useRef, useCallback } from "react";
import { Plus, Minus, X, UserPlus, Loader2, Info, XCircle, Save } from "lucide-react";
import { API_BASE_PATH } from "../apiHelper";
import FastAPIClient from "../services/apiClient";
import { useToast } from "../hooks/useToast";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import { useNavigate } from "react-router-dom";

const client = new FastAPIClient();

const NewCustomer = ({ onSuccess = () => {}, selectedStore }) => {
    const navigate = useNavigate()
    const { showToast } = useToast();

    const [user, setUser] = useState(null);
    const [rows, setRows] = useState([
        { first_name: '', last_name: '', email: '', phone: '', creation_date: new Date().toISOString().split("T")[0] }
    ]);
    const [loading, setLoading] = useState(false);
    const [failedInvites, setFailedInvites] = useState([]);
    const failedTableRef = useRef(null);
    const [pageError, setPageError] = useState(null); // For general page errors (e.g., auth)

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to add customers.", "info");
            setPageError("Authentication required. Please log in.");
            navigate('/login');
        } else {
            // console.log(JSON.parse(lcUser))
            setUser(JSON.parse(lcUser));
        }
    }, [showToast]);

    const updateRow = useCallback((index, key, value) => {
        const newRows = [...rows];
        newRows[index][key] = value;
        setRows(newRows);
    }, [rows]);

    const addRow = useCallback(() => {
        setRows(prevRows => [...prevRows, { first_name: '', last_name: '', email: '', phone: '', creation_date: new Date().toISOString().split("T")[0] }]);
    }, []);

    const removeRow = useCallback((index) => {
        setRows(prevRows => prevRows.filter((_, i) => i !== index));
        setFailedInvites(prevFailed => prevFailed.filter((_, i) => i !== index)); // Also remove associated failed entry if any
    }, []);

    const validateRows = useCallback(() => {
        let isValid = true;
        // const newErrors = []; // No longer needed for validation errors per row
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if(!selectedStore) {
            showToast("No store selected. Please select a store in POS before adding customers.", "error");
            isValid = false;
        }

        rows.forEach((row, index) => {
            // const rowErrors = {}; // No longer needed for validation errors per row
            if (!row.first_name.trim()) {
                // rowErrors.first_name = "First name is required."; // No longer storing row-specific errors
                isValid = false;
            }
            if (!row.last_name.trim()) {
                // rowErrors.last_name = "Last name is required.";
                isValid = false;
            }

            if(!row.phone || !row.phone.trim()) {
                showToast(`Phone number is required for row ${index + 1}`, "error");
                isValid = false
            }

            // Email is now optional, only validate format if provided
            if (row.email.trim() && !emailRegex.test(row.email)) {
                // rowErrors.email = "Invalid email format.";
                isValid = false;
            }
            // Phone and date are optional and no format validation is applied here.

            if (!isValid && !rows.every(r => r.phone?.trim())) { // If any validation fails, show a general toast
                showToast("Please fill in all required fields (Name & Phone).", "error");
            }
        });
        return isValid;
    }, [rows, selectedStore, showToast]);


    const handleSubmit = async () => {
        setLoading(true);
        setFailedInvites([]);
        setPageError(null);

        if (!validateRows()) {
            setLoading(false);
            return; // Validation failed, toast already shown
        }

        const payload = rows.map(r => ({
            first_name: r.first_name,
            last_name: r.last_name,
            email: r.email || null, // Ensure null if empty for optional fields
            phone: r.phone.trim(), // Ensure null if empty for optional fields
            creation_date: r.creation_date,
            referer_id: user?.users_id,
            store_id: selectedStore?.store_id || null, 
        }));

        // console.log("Submitting customers payload:", payload);

        try {
            // Assuming a batch creation endpoint for customers
            const response = await client.postData(`${API_BASE_PATH}/auth/new-customers`, payload); // Adjust endpoint as per your API
            // console.log("Customers created successfully:", response);
            showToast("Customers added successfully!", 'success');
            setRows([{ first_name: '', last_name: '', email: '', phone: '', creation_date: new Date().toISOString().split("T")[0] }]); // Reset form

            if (onSuccess) onSuccess();
        } catch (err) {
            console.error("Error submitting new customers:", err);
            const errorMessage = err?.response?.data?.detail || `Failed to add customers: ${err.message || err}`;
            showToast(errorMessage, 'error');

            // Handle specific failed invitations if the backend provides them
            if (err.response?.data?.failed_customers && Array.isArray(err.response.data.failed_customers)) {
                setFailedInvites(err.response.data.failed_customers);
                // Scroll to failed invites table if it appears
                setTimeout(() => {
                    failedTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            } else {
                setPageError(errorMessage); // Set general error if no specific failed invites
            }
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to add new customers.</p>
                <Button onClick={() => navigate('/login')} variant="primary">
                    Go to Login
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-1 justify-center items-start py-10 px-4 sm:px-6 lg:px-8 bg-gray-100 min-h-screen">
            <Card className="w-full max-w-4xl mx-auto p-8 flex flex-col">
                <h2 className="text-3xl font-bold text-text-primary mb-8 text-center flex items-center justify-center gap-3">
                    <UserPlus size={32} className="text-gmail-blue" /> Add New Customers
                </h2>
                {selectedStore && (
                    <div className="text-sm text-center text-gray-600">
                        Creating customer(s) for store:
                        <span className="font-semibold text-gmail-blue ml-1">
                            {selectedStore.identifier}
                        </span>
                    </div>
                )}
                <p className="text-text-secondary text-center mb-8 max-w-md mx-auto">
                    Quickly add multiple customer records to your system.
                </p>

                {pageError && (
                    <div className="bg-gmail-red/10 border border-gmail-red text-gmail-red p-3 rounded-md mb-6 text-center">
                        <XCircle size={20} className="inline-block mr-2" />
                        {pageError}
                    </div>
                )}

                <div className="overflow-x-auto overflow-y-auto max-h-[500px] custom-scrollbar pb-4 mb-4 rounded-lg border border-gray-200">
                    <table className="min-w-full table-auto text-sm text-text-primary">
                        <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 shadow-sm z-10">
                            <tr>
                                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">First Name</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Last Name</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Email</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Phone</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Date</th>
                                <th className="px-4 py-3 text-center font-semibold text-gray-700 whitespace-nowrap">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map((row, i) => (
                                <tr key={i} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-2">
                                        <Input
                                            type="text"
                                            value={row.first_name}
                                            placeholder="First Name"
                                            onChange={(e) => updateRow(i, 'first_name', e.target.value)}
                                            className="!py-1 !px-2 !text-sm"
                                            containerClassName="!mb-0" // Remove default margin-bottom
                                        />
                                    </td>
                                    <td className="px-4 py-2">
                                        <Input
                                            type="text"
                                            value={row.last_name}
                                            placeholder="Last Name"
                                            onChange={(e) => updateRow(i, 'last_name', e.target.value)}
                                            className="!py-1 !px-2 !text-sm"
                                            containerClassName="!mb-0"
                                        />
                                    </td>
                                    <td className="px-4 py-2">
                                        <Input
                                            type="email"
                                            value={row.email}
                                            placeholder="Email"
                                            onChange={(e) => updateRow(i, 'email', e.target.value)}
                                            className="!py-1 !px-2 !text-sm"
                                            containerClassName="!mb-0"
                                            // No 'required' prop here, making it optional
                                        />
                                    </td>
                                    <td className="px-4 py-2">
                                        <Input
                                            type="text"
                                            value={row.phone}
                                            placeholder="Phone (Required)"
                                            onChange={(e) => updateRow(i, 'phone', e.target.value)}
                                            className="!py-1 !px-2 !text-sm border-orange-200 focus:border-orange-500"
                                            containerClassName="!mb-0"
                                            required
                                        />
                                    </td>
                                    <td className="px-4 py-2">
                                        <Input
                                            type="date"
                                            value={row.creation_date}
                                            onChange={(e) => updateRow(i, 'creation_date', e.target.value)}
                                            className="!py-1 !px-2 !text-sm"
                                            containerClassName="!mb-0"
                                        />
                                    </td>
                                    <td className="px-2 text-center align-middle">
                                        {rows.length > 1 && (
                                            <Button
                                                onClick={() => removeRow(i)}
                                                variant="ghost"
                                                size="sm"
                                                className="p-1.5 rounded-full text-gmail-red hover:bg-red-100"
                                                aria-label="Remove row"
                                            >
                                                <X size={16} />
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-between items-center mt-4">
                    <Button onClick={addRow} variant="secondary" className="flex items-center gap-2 px-4 py-2">
                        <Plus size={20} /> Add Row
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="primary"
                        size="lg"
                        disabled={loading}
                        className="flex items-center gap-2 px-6 py-3"
                    >
                        {loading ? (
                            <Loader2 className="animate-spin h-5 w-5" />
                        ) : (
                            <Save size={20} />
                        )}
                        {loading ? 'Submitting...' : 'Submit Customers'}
                    </Button>
                </div>

                {failedInvites.length > 0 && (
                    <div ref={failedTableRef} className="mt-10 border-t border-gray-200 pt-6">
                        <h3 className="text-xl font-semibold mb-4 text-gmail-red flex items-center gap-2">
                            <XCircle size={24} /> Failed Submissions
                        </h3>
                        <div className="overflow-x-auto rounded-lg border border-red-300 shadow-sm">
                            <table className="min-w-full table-auto text-sm bg-red-50">
                                <thead className="text-left bg-red-100 border-b border-red-300">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold text-red-800 whitespace-nowrap">Email</th>
                                        <th className="px-4 py-3 font-semibold text-red-800 whitespace-nowrap">Reason</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-red-200">
                                    {failedInvites.map((fail, idx) => (
                                        <tr key={idx}>
                                            <td className="px-4 py-2 text-red-700">{fail.email}</td>
                                            <td className="px-4 py-2 text-red-700">{fail.reason || "Unknown error"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default NewCustomer;